import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import DraftsIcon from '@mui/icons-material/Drafts';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import ContactsIcon from '@mui/icons-material/Contacts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import InvoiceWorkflowTracker from '../components/InvoiceWorkflowTracker';

interface LinkedVendorInvoice {
  id: number;
  pdf_filename: string | null;
  vendor_name: string | null;
  statement_number: string | null;
  invoice_date: string | null;
  total_amount: number | null;
  validation_status: string | null;
  created_at: string;
}

interface S3Attachment {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  url: string;
}

interface VendorMatch {
  match_method: string | null;
  matched_contact: {
    id: number;
    full_name: string;
    email: string;
    u_vnddispl: string | null;
    vendor_id: string;
    location: string;
    contact_type: string;
    vendor_name: string | null;
  } | null;
  vendor_contact_detail: {
    id: number;
    vendor_name: string;
    vendor_id: string;
    location: string;
    has_invoice_number_region: boolean;
    has_invoice_amount_region: boolean;
  } | null;
}

interface OutlookEmailDetail {
  id: number;
  message_id: string;
  email_account: string;
  folder: string;
  subject: string;
  body_preview: string;
  received_at: string;
  has_attachments: boolean;
  is_read: boolean;
  from_name: string;
  from_address: string;
  email_details?: {
    body?: {
      content: string;
      contentType: string;
    };
    to?: Array<{
      emailAddress: {
        name: string;
        address: string;
      };
    }>;
    cc?: Array<{
      emailAddress: {
        name: string;
        address: string;
      };
    }>;
    sentDateTime?: string;
  };
  attachments?: Array<{
    id: string;
    name: string;
    contentType: string;
    size: number;
  }>;
  s3_attachments?: S3Attachment[];
  vendor_invoices?: LinkedVendorInvoice[];
  vendor_match?: VendorMatch;
}

const OutlookEmailDetailPage: React.FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [email, setEmail] = useState<OutlookEmailDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [reimporting, setReimporting] = useState<boolean>(false);
  const [resyncing, setResyncing] = useState<boolean>(false);
  const [diagLog, setDiagLog] = useState<string | null>(null);
  const [diagCopied, setDiagCopied] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);

  const fetchEmailDetail = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/vendors/outlook_emails/${id}`);
      setEmail(response.data);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch email details';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReimportInvoice = async () => {
    setReimporting(true);
    try {
      await axios.post(`/vendors/outlook_emails/${id}/reimport_invoice`);
      showSuccessSnackBar('Invoice reimport queued — refresh in a moment to see the result');
      await fetchEmailDetail();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to queue reimport';
      showErrorSnackBar(errorMessage);
    } finally {
      setReimporting(false);
    }
  };

  const fetchDiagnosticsLog = async () => {
    setDiagLoading(true);
    setDiagLog(null);
    // Poll for the diagnostics log — the job runs async so the log may not be ready yet
    const maxAttempts = 20;
    const delayMs = 3000;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      try {
        const response: any = await axios.get(`/vendors/outlook_emails/${id}/resync_diagnostics`);
        if (response.data.found && response.data.content?.includes('Resync Complete')) {
          setDiagLog(response.data.content);
          setDiagLoading(false);
          return;
        }
      } catch {
        // keep polling
      }
    }
    setDiagLoading(false);
    showErrorSnackBar('Diagnostics log not ready yet — try the Copy Diagnostics Log button again in a moment');
  };

  const handleCopyDiagLog = async () => {
    if (!diagLog) return;
    await navigator.clipboard.writeText(diagLog);
    setDiagCopied(true);
    setTimeout(() => setDiagCopied(false), 2000);
  };

  const handleResyncEmail = async () => {
    setResyncing(true);
    setDiagLog(null);
    try {
      await axios.post(`/vendors/outlook_emails/${id}/resync_email`);
      showSuccessSnackBar('Full email resync queued — diagnostics log will be available shortly');
      fetchDiagnosticsLog(); // start polling in background (don't await)
      await fetchEmailDetail();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to queue resync';
      showErrorSnackBar(errorMessage);
    } finally {
      setResyncing(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (!email) return;

    try {
      await axios.put(`/vendors/outlook_emails/${id}`, {
        outlook_email: {
          is_read: true,
        },
      });
      showSuccessSnackBar('Email marked as read');
      setEmail({ ...email, is_read: true });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to update email';
      showErrorSnackBar(errorMessage);
    }
  };

  useEffect(() => {
    fetchEmailDetail();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!email) {
    return (
      <Box>
        <Typography variant="h6">Email not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/vendors/outlook-emails')}>
          Back to Emails
        </Button>
      </Box>
    );
  }

  const formatEmailList = (emailList?: Array<{ emailAddress: { name: string; address: string } }>) => {
    if (!emailList || emailList.length === 0) return '-';
    return emailList.map((e) => `${e.emailAddress.name} <${e.emailAddress.address}>`).join(', ');
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Box style={{ width: '100%' }}>
      <Box mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/vendors/outlook-emails')}
          style={{ marginBottom: 16 }}
        >
          Back to Emails
        </Button>

        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h5" component="h5">
              Email Details
            </Typography>
          </Grid>
          <Grid item>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={resyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleResyncEmail}
                disabled={resyncing}
              >
                {resyncing ? 'Resyncing...' : 'Resync from Outlook'}
              </Button>
              {!email.is_read && (
                <Button variant="contained" startIcon={<DraftsIcon />} onClick={handleMarkAsRead}>
                  Mark as Read
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {(diagLoading || diagLog) && (
        <Card sx={{ mb: 2, bgcolor: 'grey.50' }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">Resync Diagnostics Log</Typography>
              <Box display="flex" gap={1}>
                {diagLog && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyDiagLog}
                    color={diagCopied ? 'success' : 'primary'}
                  >
                    {diagCopied ? 'Copied!' : 'Copy Diagnostics Log'}
                  </Button>
                )}
              </Box>
            </Box>
            {diagLoading && !diagLog && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Waiting for resync to complete...
                </Typography>
              </Box>
            )}
            {diagLog && (
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: 'auto',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Typography
                  component="pre"
                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}
                >
                  {diagLog}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ReceiptIcon color="action" />
            <Typography variant="h6" style={{ flexGrow: 1 }}>
              Vendor Invoice
            </Typography>
          </Box>
          {email.vendor_invoices && email.vendor_invoices.length > 0 ? (
            <Grid container spacing={2}>
              {email.vendor_invoices.map((invoice) => (
                <Grid item xs={12} key={invoice.id}>
                  <Box
                    p={2}
                    border={1}
                    borderColor="grey.300"
                    borderRadius={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {invoice.vendor_name || '(Vendor pending)'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {invoice.statement_number ? `${invoice.statement_number}` : 'Statement # pending'}
                        {invoice.total_amount != null
                          ? ` · $${parseFloat(String(invoice.total_amount)).toFixed(2)}`
                          : ''}
                        {invoice.pdf_filename ? ` · ${invoice.pdf_filename}` : ''}
                      </Typography>
                      <Chip label={invoice.validation_status || 'pending'} size="small" style={{ marginTop: 4 }} />
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ReceiptIcon />}
                      onClick={() => history.push(`/vendors/invoices/${invoice.id}`)}
                    >
                      View Invoice
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="textSecondary">
                No vendor invoice has been created from this email.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={reimporting ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={handleReimportInvoice}
                disabled={reimporting || !email.has_attachments}
              >
                {reimporting ? 'Queuing...' : 'Regenerate Invoice'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ContactsIcon color="action" />
            <Typography variant="h6" style={{ flexGrow: 1 }}>
              Vendor Contact Match
            </Typography>
          </Box>
          {email.vendor_match ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Matched Contact
                </Typography>
                {email.vendor_match.matched_contact ? (
                  <Box p={2} border={1} borderColor="grey.300" borderRadius={1}>
                    <Typography variant="body1" fontWeight={500}>
                      {email.vendor_match.matched_contact.vendor_name || email.vendor_match.matched_contact.full_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {email.vendor_match.matched_contact.email}
                    </Typography>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip label={`Vendor: ${email.vendor_match.matched_contact.vendor_id}`} size="small" />
                      <Chip label={email.vendor_match.matched_contact.location} size="small" />
                      <Chip label={email.vendor_match.matched_contact.contact_type} size="small" variant="outlined" />
                      {email.vendor_match.match_method && (
                        <Chip
                          label={`Match: ${email.vendor_match.match_method}`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {email.vendor_match.matched_contact.u_vnddispl && (
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Display Name: {email.vendor_match.matched_contact.u_vnddispl}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No vendor contact matched for this sender.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Vendor Contact Detail
                </Typography>
                {email.vendor_match.vendor_contact_detail ? (
                  <Box p={2} border={1} borderColor="grey.300" borderRadius={1}>
                    <Typography variant="body1" fontWeight={500}>
                      {email.vendor_match.vendor_contact_detail.vendor_name}
                    </Typography>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip label={`Vendor: ${email.vendor_match.vendor_contact_detail.vendor_id}`} size="small" />
                      <Chip label={email.vendor_match.vendor_contact_detail.location} size="small" />
                    </Box>
                    <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {email.vendor_match.vendor_contact_detail.has_invoice_number_region ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <CancelIcon fontSize="small" color="disabled" />
                        )}
                        <Typography variant="body2">Invoice Number Region</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {email.vendor_match.vendor_contact_detail.has_invoice_amount_region ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <CancelIcon fontSize="small" color="disabled" />
                        )}
                        <Typography variant="body2">Invoice Amount Region</Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No vendor contact detail configured for this vendor.
                  </Typography>
                )}
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Vendor match data not available.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="h6" style={{ flexGrow: 1 }}>
                  {email.subject || '(No Subject)'}
                </Typography>
                {email.is_read ? (
                  <Chip label="Read" size="small" icon={<DraftsIcon fontSize="small" />} />
                ) : (
                  <Chip label="Unread" color="primary" size="small" icon={<EmailIcon fontSize="small" />} />
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  From:
                </Typography>
              </Box>
              <Typography variant="body1">{email.from_name || '-'}</Typography>
              <Typography variant="body2" color="textSecondary">
                {email.from_address || '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  Received:
                </Typography>
              </Box>
              <Typography variant="body1">{formatDateTime(email.received_at)}</Typography>
            </Grid>

            {email.email_details?.to && email.email_details.to.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  To:
                </Typography>
                <Typography variant="body1">{formatEmailList(email.email_details.to)}</Typography>
              </Grid>
            )}

            {email.email_details?.cc && email.email_details.cc.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  CC:
                </Typography>
                <Typography variant="body1">{formatEmailList(email.email_details.cc)}</Typography>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  Account:
                </Typography>
              </Box>
              <Typography variant="body1">{email.email_account}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <FolderIcon fontSize="small" color="action" />
                <Typography variant="body2" color="textSecondary">
                  Folder:
                </Typography>
              </Box>
              <Typography variant="body1">{email.folder}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {email.s3_attachments && email.s3_attachments.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AttachFileIcon />
              <Typography variant="h6">Attachments ({email.s3_attachments.length})</Typography>
            </Box>
            <Grid container spacing={2}>
              {email.s3_attachments.map((attachment) => {
                const isImage = attachment.content_type?.startsWith('image/');
                const isPdf = attachment.content_type === 'application/pdf';

                return (
                  <Grid item xs={12} key={attachment.id}>
                    <Box p={2} border={1} borderColor="grey.300" borderRadius={1}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={isImage || isPdf ? 2 : 0}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {attachment.filename}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {attachment.content_type} • {formatFileSize(attachment.byte_size)}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                          {(isImage || isPdf) && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={() => window.open(attachment.url, '_blank')}
                            >
                              Preview
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            href={attachment.url}
                            download={attachment.filename}
                          >
                            Download
                          </Button>
                        </Box>
                      </Box>
                      {isImage && (
                        <Box mt={2}>
                          <img
                            src={attachment.url}
                            alt={attachment.filename}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '400px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Message
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          {email.email_details?.body?.contentType === 'html' ? (
            <Box
              sx={{
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                },
                '& a': {
                  color: 'primary.main',
                },
              }}
              dangerouslySetInnerHTML={{ __html: email.email_details.body.content }}
            />
          ) : (
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {email.email_details?.body?.content || email.body_preview || '(No content)'}
            </Typography>
          )}
        </CardContent>
      </Card>

      <InvoiceWorkflowTracker emailId={id} />
    </Box>
  );
};

export default OutlookEmailDetailPage;
